import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from './message.service';
import { LoggerService } from './logger.service';
import {
  AppError,
  ErrorHttpMessage,
  HttpAppError,
} from '../../models/error.model';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorResponse } from '../../models/error-response.model';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _logger: LoggerService = inject(LoggerService);
  private readonly _router: Router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _errorCache = new Map<
    string,
    { timestamp: number; count: number }
  >();
  private readonly _errorDebounceTime = 5000; // 5 seconds
  private readonly _maxErrorsPerPeriod = 1;

  private readonly _errorMessages: ErrorHttpMessage[] = [
    { statusRange: [0, 0], message: 'Cannot connect to the server.' },
    { statusRange: [400, 400], message: 'Bad Request' },
    { statusRange: [401, 401], message: 'Unauthorized' },
    { statusRange: [403, 403], message: 'Forbidden' },
    { statusRange: [404, 404], message: 'Not Found' },
    { statusRange: [408, 408], message: 'Request Timeout' },
    { statusRange: [429, 429], message: 'Too Many Requests' },
    { statusRange: [400, 499], message: 'Client Error' },
    { statusRange: [500, 500], message: 'Internal Server Error' },
    { statusRange: [502, 502], message: 'Bad Gateway' },
    { statusRange: [503, 503], message: 'Service Unavailable' },
    { statusRange: [504, 504], message: 'Gateway Timeout' },
    { statusRange: [500, Infinity], message: 'Server Error' },
  ];

  constructor() {
    this.setupRouteChangeListener();
  }

  handleError(error: Error | HttpErrorResponse): void {
    if (error instanceof HttpErrorResponse) {
      const appError = this.handleHttpError(error);

      if (this.isErrorResponse(error)) {
        this.showErrorMessage(appError);
      }

      this._logger.error('Error:', appError);
    } else {
      const appError = this.handleGenericError(error);
      if (this.shouldShowError(appError)) {
        this.showErrorMessage(appError);
        this._logger.error('Error:', appError);
      }
    }
  }

  private handleHttpError(error: HttpErrorResponse): HttpAppError {
    if (this.isErrorResponse(error)) {
      const errorResponse = error.error as ErrorResponse;
      const userMessage = errorResponse.errors?.length
        ? errorResponse.errors.join(', ') // Concatenamos los errores
        : errorResponse.title || 'An unknown error occurred.';

      return {
        name: 'HttpError',
        message: error.message,
        code: `HTTP_${error.status}`,
        userMessage,
        status: error.status,
      };
    }

    const userMessage = this.getUserMessageForHttpError(error);
    return {
      name: 'HttpError',
      message: error.message,
      code: `HTTP_${error.status}`,
      userMessage,
      status: error.status,
    };
  }

  private isErrorResponse(error: HttpErrorResponse): boolean {
    return (
      error.error &&
      typeof error.error === 'object' &&
      'type' in error.error &&
      'title' in error.error &&
      'status' in error.error &&
      'detail' in error.error &&
      Array.isArray(error.error.errors)
    );
  }

  private getUserMessageForHttpError(error: HttpErrorResponse): string {
    return (
      this._errorMessages.find(
        ({ statusRange }) =>
          error.status >= statusRange[0] && error.status <= statusRange[1]
      )?.message ?? 'An unknown error occurred.'
    );
  }

  private handleGenericError(error: Error): AppError {
    return {
      name: error.name,
      message: error.message,
      code: 'GENERIC_ERROR',
      userMessage: 'An unknown error occurred.',
    };
  }

  private showErrorMessage(error: AppError): void {
    this._messageService.error(error.userMessage);
  }

  private shouldShowError(error: AppError): boolean {
    const now = Date.now();
    const cacheKey = `${error.code}_${error.userMessage}`;
    const cachedError = this._errorCache.get(cacheKey);

    if (!cachedError) {
      this._errorCache.set(cacheKey, { timestamp: now, count: 1 });
      return true;
    }

    if (now - cachedError.timestamp > this._errorDebounceTime) {
      this._errorCache.set(cacheKey, { timestamp: now, count: 1 });
      return true;
    }

    if (cachedError.count < this._maxErrorsPerPeriod) {
      this._errorCache.set(cacheKey, {
        timestamp: cachedError.timestamp,
        count: cachedError.count + 1,
      });
      return true;
    }

    return false;
  }

  private setupRouteChangeListener() {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this.clearErrorCache());
  }

  private clearErrorCache(): void {
    this._errorCache.clear();
  }
}
